import { Fragment, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Unity, useUnityContext } from "react-unity-webgl";
import { getGameAssetsUrl, getPublisherLogo } from "../../utilities/Helpers";
import { Helmet } from "react-helmet";

// This is needed to avoid a unityInstance is not defined error
declare global {
    interface Window {
        unityInstance: any;
    }
}
window.unityInstance = window.unityInstance || {};

const UnityGameComponent = ({ game }: { game: Game }) => {
    const navigate = useNavigate();
    const gameStaticUrl = getGameAssetsUrl(game.id);
    const gameAssetsUrl = `/games/${game.id}`;
    const [backgroundId] = useState(Math.floor(Math.random() * ((game.backgrounds - 1) + 1) + 1));

    const {
        unityProvider,
        loadingProgression,
        isLoaded,
        UNSAFE__unityInstance, // Needed to avoid a unityInstance is not defined error
        UNSAFE__detachAndUnloadImmediate: detachAndUnloadImmediate, // Using unsafe method due to a bug. https://github.com/jeffreylanters/react-unity-webgl/issues/22
    } = useUnityContext({
        loaderUrl: `${gameAssetsUrl}/content/game.loader.js`,
        dataUrl: `${gameAssetsUrl}/content/game.data.unityweb`,
        frameworkUrl: `${gameAssetsUrl}/content/game.framework.js.unityweb`,
        codeUrl: `${gameAssetsUrl}/content/game.wasm.unityweb`,
        productName: game.name,
        companyName: game.publisher.name,
        productVersion: "1.0",
        cacheControl: handleCacheControl,
    });

    // This is to avoid an error that crashes everything, need to find a better way to handle it when the package is updated
    window.unityInstance = UNSAFE__unityInstance;

    useEffect(() => {
        window.addEventListener("resize", resize);
        resize();

        return () => {
            window.removeEventListener("resize", resize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isLoaded) {
            return () => {
                detachAndUnloadImmediate();
            };
        }
    }, [
        isLoaded,
        detachAndUnloadImmediate,
    ]);

    const resize = () => {
        const unityElement = document.getElementById("unity") as HTMLInputElement;
        const width = parseFloat(window.getComputedStyle(unityElement).width);
        unityElement.setAttribute(
            "style",
            "height: " + (width / 16) * 9 + "px !important"
        );
    };

    function handleCacheControl(url: string) {
        if (url.match(/\.data\.unityweb/) || url.match(/\.bundle\.unityweb/)) {
            return "must-revalidate";
        }
        return "no-store";
    }

    function handleLeaveGame() {
        detachAndUnloadImmediate();
        console.log("navigate to home");
        navigate("/");
    }

    return (
        <>
            <Helmet>
                <style>{`
                    html {
                        background-image: url(${gameStaticUrl}/background${backgroundId}.png);
                        background-repeat: no-repeat;
                        background-size: cover;
                    }
                `}</style>
            </Helmet>
            <Row className="justify-content-between mb-3 text-center">
                <Col xs={3}>
                    <button className="dg-btn dg-btn-mid-dark dg-btn-block" onClick={handleLeaveGame}>
                        Leave Game
                    </button>
                </Col>
                <Col>
                    <img src={`${gameStaticUrl}/logo.png`} alt={game?.name} height={72} />
                </Col>
                <Col xs={3}>
                    <a href={game?.publisher.website} className="dg-btn dg-btn-mid-dark dg-btn-block" target="_blank" rel="noreferrer noopener" title={game?.publisher.name}>
                        <img src={getPublisherLogo(game?.publisher.name)} alt={game?.publisher.name} height={32} /> {game?.publisher.name}
                    </a>
                </Col>
            </Row>
            <Card className="card-widget b-3" id="game-widget">
                <Card.Body className="m-0 p-0">
                    <div className="runner-table mx-auto" id="unity">
                        <Fragment>
                            {!isLoaded && (
                                <p className="full-center">
                                    Loading Application... {Math.round(loadingProgression * 100)}%
                                </p>
                            )}
                            <Unity
                                unityProvider={unityProvider}
                                style={{
                                    visibility: isLoaded ? "visible" : "hidden"
                                }}
                            />
                        </Fragment>
                    </div>
                </Card.Body>
                <Card.Footer className="game-socials text-end">
                    <a href={game?.publisher.twitter} target="_blank" rel="noreferrer noopener" title="Twitter" className="dg-twitter">&nbsp;</a>
                    <a href={game?.publisher.website} target="_blank" rel="noreferrer noopener" title={game?.publisher.name} className="dg-link">&nbsp;</a>
                </Card.Footer>
            </Card>
        </>
    );
};

export default UnityGameComponent;
